import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  Alert,
  alpha,
  Container,
  Tooltip,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Avatar,
} from '@mui/material';
import {
  AccessTime,
  HolidayVillage,
  Info,
  PersonOff,
  EventBusy,
  CheckCircle,
  Cancel,
  Refresh,
  Assessment,
  BarChart,
  Visibility,
  Coffee,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, settingsAPI, employeesAPI, attendanceAPI, API_URL } from '../services/api';

// Import new components
import ReportFilters from '../components/ReportFilters';
import ReportStats from '../components/ReportStats';
import ReportContent from '../components/ReportContent';

const Reports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'hr';
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportType, setReportType] = useState(isAdmin ? 'daily' : 'monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [reportData, setReportData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedDays, setExpandedDays] = useState({});
  const [showAbsentDialog, setShowAbsentDialog] = useState(false);
  const [selectedAbsentEmployees, setSelectedAbsentEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedEmployeesForMarking, setSelectedEmployeesForMarking] = useState([]);
  const [markingPresent, setMarkingPresent] = useState(false);
  const [showPresentDialog, setShowPresentDialog] = useState(false);
  const [selectedPresentEmployees, setSelectedPresentEmployees] = useState([]);
  const [selectedEmployeesForAbsent, setSelectedEmployeesForAbsent] = useState([]);
  const [markingAbsent, setMarkingAbsent] = useState(false);
  const [weekendOff, setWeekendOff] = useState(true);
  const [firstSaturdayWork, setFirstSaturdayWork] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showStats, setShowStats] = useState(true);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportOptions, setExportOptions] = useState({
    includeBreaks: true,
    includeHolidays: true,
    includeWeekends: false,
  });
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedEmployeesBulk, setSelectedEmployeesBulk] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showEmployeeDetailDialog, setShowEmployeeDetailDialog] = useState(false);
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [employeeDetailAttendance, setEmployeeDetailAttendance] = useState([]);
  const [loadingEmployeeDetail, setLoadingEmployeeDetail] = useState(false);
  const [selectedDayRecord, setSelectedDayRecord] = useState(null);
  const [showDayDetailDialog, setShowDayDetailDialog] = useState(false);
  const [showBarChart, setShowBarChart] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const itemsPerPage = 31;

  // Base URL for selfies
  const baseURL = API_URL.replace('/api', '');

  // Helper function to check if a date is a weekend day
  const isWeekendDay = (date) => {
    const dayOfWeek = date.getDay();
    if (!weekendOff) return false;
    if (dayOfWeek === 0) return true;
    if (dayOfWeek === 6) {
      if (!firstSaturdayWork) return true;
      const day = date.getDate();
      if (day > 7) return true;
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const firstDayOfWeek = firstDay.getDay();
      const daysToFirstSaturday = (6 - firstDayOfWeek) % 7;
      const firstSaturdayDate = daysToFirstSaturday + 1;
      return day !== firstSaturdayDate;
    }
    return false;
  };

  // Real-time update timer for active attendance
  useEffect(() => {
    if (showDetailDialog) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showDetailDialog]);

  // Initialize component data
  useEffect(() => {
    loadEmployees();
    loadTimezone();
    loadWeekendSettings();
  }, []);

  // Auto-load weekly report when week start changes
  useEffect(() => {
    if (reportType === 'weekly' && selectedWeekStart) {
      loadReport();
    }
  }, [selectedWeekStart]);

  // Auto-load when report type or filters change
  useEffect(() => {
    // Reset week start when changing from weekly to other types
    if (reportType !== 'weekly') {
      setSelectedWeekStart('');
    }
    // Load report when dependencies change
    loadReport();
  }, [reportType, selectedMonth, selectedYear, selectedEmployee, refreshKey]);

  // Calculate real-time break time
  const getRealTimeBreakTime = (attendance) => {
    if (!attendance || !attendance.breaks) return 0;
    const today = new Date().toISOString().split('T')[0];
    const attendanceDate = attendance.date ? attendance.date.toString().split('T')[0] : null;
    if (attendanceDate !== today) {
      return attendance.total_break_time || 0;
    }
    let totalMinutes = 0;
    attendance.breaks.forEach(brk => {
      if (brk.status === 'active') {
        const startTime = new Date(brk.start_time);
        const elapsed = Math.floor((currentTime - startTime) / 60000);
        totalMinutes += elapsed;
      } else if (brk.duration) {
        totalMinutes += brk.duration;
      }
    });
    return totalMinutes;
  };

  // Calculate real-time working time
  const getRealTimeWorkingTime = (attendance) => {
    if (!attendance || !attendance.login_time) return 0;
    const today = new Date().toISOString().split('T')[0];
    const attendanceDate = attendance.date ? attendance.date.toString().split('T')[0] : null;
    if (attendanceDate !== today) {
      return attendance.net_working_time || 0;
    }
    const loginTime = new Date(attendance.login_time);
    const logoutTime = attendance.logout_time ? new Date(attendance.logout_time) : currentTime;
    let totalMinutes = Math.floor((logoutTime - loginTime) / 60000);
    const breakTime = getRealTimeBreakTime(attendance);
    totalMinutes = Math.max(0, totalMinutes - breakTime);
    return totalMinutes;
  };

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 100, status: 'active' });
      if (response.data && response.data.success) {
        const employeeList = response.data.data?.employees || response.data.data;
        if (Array.isArray(employeeList)) {
          setEmployees(employeeList);
        } else if (employeeList?.rows && Array.isArray(employeeList.rows)) {
          setEmployees(employeeList.rows);
        } else {
          setEmployees([]);
        }
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    }
  };

  const loadTimezone = async () => {
    try {
      const tzResponse = await settingsAPI.getByKey('timezone');
      if (tzResponse.data.success && tzResponse.data.data) {
        setTimezone(tzResponse.data.data.value || 'Asia/Kolkata');
      }
    } catch (error) {
      console.log('Timezone not set, using default');
    }
  };

  const loadWeekendSettings = async () => {
    try {
      const [weekendRes, satWorkRes] = await Promise.all([
        settingsAPI.getByKey('weekend_off'),
        settingsAPI.getByKey('first_saturday_work')
      ]);
      if (weekendRes.data.success && weekendRes.data.data) {
        setWeekendOff(weekendRes.data.data.value === 'true');
      }
      if (satWorkRes.data.success && satWorkRes.data.data) {
        setFirstSaturdayWork(satWorkRes.data.data.value === 'true');
      }
    } catch (error) {
      console.log('Weekend settings not loaded, using defaults');
    }
  };

  const handleReportTypeChange = (newType) => {
    console.log('Changing report type from', reportType, 'to', newType);
    // Reset all report data first
    setReportData(null);
    setChartData(null);
    setCurrentPage(1);
    
    // Set the new report type
    setReportType(newType);
    
    // Reset employee selection when switching to daily report
    if (newType === 'daily') {
      setSelectedEmployee('');
    }
    
    // Initialize week start for weekly report
    if (newType === 'weekly') {
      const today = new Date();
      const currentDay = today.getDay();
      const weekStartDate = new Date(today);
      const diff = currentDay === 0 ? -6 : 1 - currentDay;
      weekStartDate.setDate(today.getDate() + diff);
      const weekStartStr = weekStartDate.toISOString().split('T')[0];
      setSelectedWeekStart(weekStartStr);
    }
  };

  const loadEmployeeAttendance = async (employeeId) => {
    try {
      const startDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
      const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];
      const response = await reportsAPI.getAttendance({
        startDate,
        endDate,
        employeeId
      });
      return response.data.data?.attendance || [];
    } catch (error) {
      console.error('Error loading employee attendance:', error);
      return [];
    }
  };

  const loadReport = useCallback(async () => {
    console.log('Loading report:', { reportType, selectedEmployee, selectedYear, selectedMonth, selectedWeekStart });
    try {
      setLoading(true);
      let response;
      let processedData = null;

      switch (reportType) {
        case 'daily':
          console.log('Fetching daily report...');
          response = await reportsAPI.getDaily(selectedEmployee || undefined);
          console.log('Daily API response:', response.data);
          
          if (response.data && response.data.success && response.data.data) {
            if (Array.isArray(response.data.data)) {
              processedData = {
                dailyData: response.data.data,
                monthTotals: response.data.monthTotals || {},
                monthLabel: response.data.monthLabel || '',
              };
            } else {
              const employeesData = response.data.data.employees || [];
              const processedEmployees = employeesData.map(emp => {
                let status = 'absent';
                if (emp.status === 'on_leave') status = 'On Leave';
                else if (emp.login_time) status = emp.status === 'late' ? 'Late' : 'Present';
                
                return {
                  ...emp,
                  status_display: status,
                  working_hours_formatted: formatWorkingHours(emp.working_hours || 0),
                  break_time_formatted: formatWorkingHours(emp.break_time || 0),
                };
              });
              
              processedData = {
                ...response.data.data,
                employees: processedEmployees,
              };
            }
          }
          break;
          
        case 'weekly':
          if (!selectedWeekStart) {
            const today = new Date();
            const currentDay = today.getDay();
            const weekStartDate = new Date(today);
            const diff = currentDay === 0 ? -6 : 1 - currentDay;
            weekStartDate.setDate(today.getDate() + diff);
            const weekStartStr = weekStartDate.toISOString().split('T')[0];
            setSelectedWeekStart(weekStartStr);
            response = await reportsAPI.getWeekly(weekStartStr, selectedEmployee || undefined);
          } else {
            response = await reportsAPI.getWeekly(selectedWeekStart, selectedEmployee || undefined);
          }
          console.log('Weekly API response:', response.data);
          
          if (response.data && response.data.success && response.data.data) {
            const weeklyData = response.data.data;
            if (weeklyData.weekTotals) {
              weeklyData.weekTotals.totalWorkingHours = formatWorkingHours(weeklyData.weekTotals.totalWorkingMinutes || 0);
              weeklyData.weekTotals.totalBreakTime = formatWorkingHours(weeklyData.weekTotals.totalBreakMinutes || 0);
            }
            processedData = weeklyData;
          }
          break;
          
        case 'monthly':
          const monthParam = selectedMonth !== undefined ? selectedMonth : new Date().getMonth();
          const yearParam = selectedYear || new Date().getFullYear();
          console.log('Fetching monthly report for:', yearParam, monthParam, 'employee:', selectedEmployee);
          
          response = await reportsAPI.getMonthly(yearParam, monthParam, selectedEmployee || undefined);
          console.log('Monthly API response:', response.data);
          
          if (response.data && response.data.success && response.data.data) {
            const monthlyData = response.data.data;
            
            let processedSummary = [];
            if (monthlyData.summary && Array.isArray(monthlyData.summary)) {
              processedSummary = monthlyData.summary.map(emp => ({
                ...emp,
                totalWorkingHours: formatWorkingHours(emp.totalWorkingMinutes || 0),
                totalBreakTime: formatWorkingHours(emp.totalBreakMinutes || 0),
                daysPresent: emp.daysPresent || emp.present || 0,
                daysLate: emp.daysLate || emp.late || 0,
                daysAbsent: emp.daysAbsent || emp.absent || 0,
              }));
            }
            
            const monthTotals = monthlyData.monthTotals || {};
            const processedTotals = {
              ...monthTotals,
              totalWorkingHours: formatWorkingHours(monthTotals.totalWorkingMinutes || 0),
              totalBreakTime: formatWorkingHours(monthTotals.totalBreakMinutes || 0),
            };
            
            processedData = {
              ...monthlyData,
              summary: processedSummary,
              monthTotals: processedTotals,
              holidaysCount: monthlyData.holidaysCount || monthlyData.holidays?.length || 0,
            };
            
            if (processedSummary.length > 0) {
              const newChartData = processedSummary.map(emp => ({
                employee: emp.employee ? `${emp.employee.first_name} ${emp.employee.last_name}` : 'Unknown',
                present: emp.daysPresent || 0,
                late: emp.daysLate || 0,
                absent: emp.daysAbsent || 0,
                onLeave: emp.leaves?.length || 0,
              }));
              setChartData(newChartData);
            }
          } else {
            console.error('Monthly API returned no data:', response.data);
            processedData = null;
          }
          break;
          
        case 'employee_wise':
          const empMonthParam = selectedMonth !== undefined ? selectedMonth : new Date().getMonth();
          const empYearParam = selectedYear || new Date().getFullYear();
          console.log('Fetching employee-wise report for:', empYearParam, empMonthParam, 'employee:', selectedEmployee);
          
          response = await reportsAPI.getMonthly(empYearParam, empMonthParam, selectedEmployee || undefined);
          console.log('Employee-wise API response:', response.data);
          
          if (response.data && response.data.success && response.data.data) {
            processedData = response.data.data;
            if (response.data.data.summary && Array.isArray(response.data.data.summary)) {
              generateChartData(response.data.data.summary);
            }
          }
          break;
          
        default:
          console.log('Fetching default daily report');
          response = await reportsAPI.getDaily(selectedEmployee || undefined);
          if (response.data && response.data.success && response.data.data) {
            processedData = response.data.data;
          }
      }
      
      setReportData(processedData);
      console.log('Report data set:', processedData);
      
    } catch (error) {
      console.error('Error loading report:', error);
      setReportData(null);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [reportType, selectedMonth, selectedYear, selectedEmployee, selectedWeekStart]);

  const generateChartData = (employeesData) => {
    if (!employeesData || !Array.isArray(employeesData)) return;
    
    const newChartData = employeesData.map(emp => ({
      employee: emp.employee ? `${emp.employee.first_name} ${emp.employee.last_name}` : `${emp.first_name} ${emp.last_name}`,
      present: emp.present || emp.daysPresent || 0,
      late: emp.late || emp.daysLate || 0,
      absent: emp.absent || 0,
      onLeave: emp.leave || 0,
    }));
    setChartData(newChartData);
  };

  const handleBulkAction = async (action) => {
    if (selectedEmployeesBulk.length === 0) {
      alert('Please select employees first');
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const response = await attendanceAPI.bulkUpdate({
        employee_ids: selectedEmployeesBulk,
        action,
        date: new Date().toISOString().split('T')[0],
      });
      
      if (response.data.success) {
        alert(`Bulk ${action} completed for ${selectedEmployeesBulk.length} employees`);
        loadReport();
      } else {
        alert('Failed to perform bulk action');
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      alert('Error performing bulk action');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {
        reportType,
        employeeId: selectedEmployee || null,
        format: exportFormat,
        includeBreaks: exportOptions.includeBreaks,
        includeHolidays: exportOptions.includeHolidays,
        includeWeekends: exportOptions.includeWeekends,
      };

      if (reportType === 'daily') {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        params.startDate = startOfMonth.toISOString().split('T')[0];
        params.endDate = endOfMonth.toISOString().split('T')[0];
      } else if (reportType === 'monthly' || reportType === 'employee_wise') {
        params.month = selectedMonth;
        params.year = selectedYear;
      }

      const response = await reportsAPI.exportReport(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'attendance-report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  };

  const getWorkingDaysInMonth = (year, month) => {
    let workingDays = 0;
    const daysInMonth = new Date(year, month, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDays++;
      }
    }
    return workingDays;
  };

  const formatWorkingHours = (minutes) => {
    if (!minutes && minutes !== 0) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const handleShowAbsentEmployees = (absentEmployees, date) => {
    setSelectedAbsentEmployees(absentEmployees || []);
    setSelectedDate(date);
    setSelectedEmployeesForMarking([]);
    setShowAbsentDialog(true);
  };

  const handleMarkSelectedPresent = async () => {
    if (selectedEmployeesForMarking.length === 0) {
      alert('Please select employees to mark present');
      return;
    }
    try {
      setMarkingPresent(true);
      const response = await attendanceAPI.bulkMarkPresent({
        date: selectedDate,
        employee_ids: selectedEmployeesForMarking
      });
      if (response.data.success) {
        alert(`Marked present completed for ${selectedEmployeesForMarking.length} employee(s)`);
        setShowAbsentDialog(false);
        setSelectedEmployeesForMarking([]);
        loadReport();
      } else {
        alert('Failed to mark employees present');
      }
    } catch (error) {
      console.error('Mark present error:', error);
      alert(error.response?.data?.message || 'Error marking employees present');
    } finally {
      setMarkingPresent(false);
    }
  };

  const handleMarkSelectedAbsent = async () => {
    if (selectedEmployeesForAbsent.length === 0) {
      alert('Please select employees to mark absent');
      return;
    }
    try {
      setMarkingAbsent(true);
      const response = await attendanceAPI.bulkMarkPresent({
        date: selectedDate,
        employee_ids: selectedEmployeesForAbsent,
        status: 'absent'
      });
      if (response.data.success) {
        alert(`Marked absent completed for ${selectedEmployeesForAbsent.length} employee(s)`);
        setShowPresentDialog(false);
        setSelectedEmployeesForAbsent([]);
        loadReport();
      } else {
        alert('Failed to mark employees absent');
      }
    } catch (error) {
      console.error('Mark absent error:', error);
      alert(error.response?.data?.message || 'Error marking employees absent');
    } finally {
      setMarkingAbsent(false);
    }
  };

  const handleBulkLeaveRequest = async () => {
    if (selectedEmployeesBulk.length === 0) {
      alert('Please select employees first');
      return;
    }
    alert(`Bulk leave request for ${selectedEmployeesBulk.length} employees would be implemented here`);
    setSelectedEmployeesBulk([]);
  };

  const handleMarkEmployeePresent = async (employeeId) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const response = await attendanceAPI.bulkMarkPresent({
        date: today,
        employee_ids: [employeeId]
      });
      if (response.data.success) {
        alert('Employee marked present for today');
        loadReport();
      } else {
        alert('Failed to mark employee present');
      }
    } catch (error) {
      console.error('Error marking present:', error);
      alert('Error marking employee present');
    }
  };

  const handleMarkEmployeeAbsent = async (employeeId) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const response = await attendanceAPI.bulkMarkPresent({
        date: today,
        employee_ids: [employeeId],
        status: 'absent'
      });
      if (response.data.success) {
        alert('Employee marked absent for today');
        loadReport();
      } else {
        alert('Failed to mark employee absent');
      }
    } catch (error) {
      console.error('Error marking absent:', error);
      alert('Error marking employee absent');
    }
  };

  const handleMarkEmployeeLeave = async (employeeId) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const response = await attendanceAPI.bulkMarkPresent({
        date: today,
        employee_ids: [employeeId],
        status: 'on_leave'
      });
      if (response.data.success) {
        alert('Employee marked on leave for today');
        loadReport();
      } else {
        alert('Failed to mark employee on leave');
      }
    } catch (error) {
      console.error('Error marking leave:', error);
      alert('Error marking employee on leave');
    }
  };

  const handleViewEmployeeDetail = async (employee) => {
    setSelectedEmployeeDetail(employee);
    setLoadingEmployeeDetail(true);
    const attendance = await loadEmployeeAttendance(employee.id);
    setEmployeeDetailAttendance(attendance);
    setLoadingEmployeeDetail(false);
    setShowEmployeeDetailDialog(true);
  };

  const handleViewDayDetail = (attendanceRecord) => {
    setSelectedDayRecord(attendanceRecord);
    setShowDayDetailDialog(true);
  };

  const handleMarkEmployeeStatus = async (employeeId, date, status) => {
    const today = new Date().toISOString().split('T')[0];
    if (date > today) {
      alert('Cannot mark attendance for future dates');
      return;
    }
    try {
      const response = await attendanceAPI.bulkMarkPresent({
        date,
        employee_ids: [employeeId],
        status: status === 'present' ? undefined : status
      });
      if (response.data.success) {
        alert(`Employee marked ${status} for ${date}`);
        const attendance = await loadEmployeeAttendance(employeeId);
        setEmployeeDetailAttendance(attendance);
        loadReport();
      } else {
        alert(`Failed to mark employee ${status}`);
      }
    } catch (error) {
      console.error(`Error marking ${status}:`, error);
      alert(`Error marking employee ${status}`);
    }
  };

  const handleMarkEmployeeStatusWithTimes = async (employeeId, date, status) => {
    const today = new Date().toISOString().split('T')[0];
    if (date > today) {
      alert('Cannot mark attendance for future dates');
      return;
    }
    try {
      const response = await attendanceAPI.markStatusWithTimes({
        employee_id: employeeId,
        date,
        status,
        auto_times: status === 'present'
      });
      if (response.data.success) {
        alert(`Employee marked ${status} for ${date}`);
        const attendance = await loadEmployeeAttendance(employeeId);
        setEmployeeDetailAttendance(attendance);
        setRefreshKey(prev => prev + 1);
      } else {
        alert(`Failed to mark employee ${status}`);
      }
    } catch (error) {
      console.error(`Error marking ${status}:`, error);
      alert(`Error marking employee ${status}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return { bg: alpha('#4CAF50', 0.15), color: '#81C784', icon: <CheckCircle sx={{ fontSize: 16 }} /> };
      case 'late': return { bg: alpha('#FF9800', 0.15), color: '#FFB74D', icon: <AccessTime sx={{ fontSize: 16 }} /> };
      case 'on_leave': return { bg: alpha('#2196F3', 0.15), color: '#90CAF9', icon: <EventBusy sx={{ fontSize: 16 }} /> };
      case 'absent': return { bg: alpha('#F44336', 0.15), color: '#EF9A9A', icon: <Cancel sx={{ fontSize: 16 }} /> };
      case 'holiday': return { bg: alpha('#9C27B0', 0.15), color: '#CE93D8', icon: <HolidayVillage sx={{ fontSize: 16 }} /> };
      default: return { bg: alpha('#9E9E9E', 0.15), color: '#B4C4A8', icon: <Info sx={{ fontSize: 16 }} /> };
    }
  };

  const handleBulkPrintLocal = (employeeIds) => {
    if (employeeIds.length === 0) {
      alert('Please select employees first');
      return;
    }
    const printWindow = window.open('', '_blank');
    const selectedEmployees = reportData?.summary?.filter(emp => employeeIds.includes(emp.employee?.id)) || [];
    const totalWorkingDays = getWorkingDaysInMonth(selectedYear, selectedMonth + 1);
    
    const printContent = `
      <html>
        <head>
          <title>Employee Attendance Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Employee Attendance Report</h2>
            <p>${getMonthName(selectedMonth)} ${selectedYear}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Present</th>
                <th>Late</th>
                <th>Absent</th>
                <th>Leave</th>
                <th>Working Hours</th>
                <th>Attendance %</th>
              </tr>
            </thead>
            <tbody>
              ${selectedEmployees.map(emp => `
                <tr>
                  <td>${emp.employee?.first_name} ${emp.employee?.last_name}</td>
                  <td>${emp.employee?.department || '-'}</td>
                  <td>${emp.present}</td>
                  <td>${emp.late}</td>
                  <td>${emp.absent}</td>
                  <td>${emp.leave}</td>
                  <td>${emp.totalWorkingHours}</td>
                  <td>${((emp.present + emp.late) / totalWorkingDays * 100).toFixed(1)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37', textAlign: 'center' }}>
        <Assessment sx={{ mr: 2, verticalAlign: 'middle' }} />
        Attendance Reports Dashboard
      </Typography>

      <ReportFilters
        isAdmin={isAdmin}
        reportType={reportType}
        handleReportTypeChange={handleReportTypeChange}
        selectedEmployee={selectedEmployee}
        setSelectedEmployee={setSelectedEmployee}
        employees={employees}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedWeekStart={selectedWeekStart}
        setSelectedWeekStart={setSelectedWeekStart}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        loading={loading}
        exportFormat={exportFormat}
        setExportFormat={setExportFormat}
        handleExport={handleExport}
        exporting={exporting}
        showExportOptions={showExportOptions}
        setShowExportOptions={setShowExportOptions}
        showStats={showStats}
        setShowStats={setShowStats}
        showBarChart={showBarChart}
        setShowBarChart={setShowBarChart}
      />

      <ReportStats
        showStats={showStats}
        reportType={reportType}
        reportData={reportData}
        isAdmin={isAdmin}
        onClose={() => setShowStats(false)}
        totalEmployees={employees.length}
      />

      <ReportContent
        loading={loading}
        reportType={reportType}
        reportData={reportData}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        isAdmin={isAdmin}
        selectedEmployeesBulk={selectedEmployeesBulk}
        setSelectedEmployeesBulk={setSelectedEmployeesBulk}
        bulkActionLoading={bulkActionLoading}
        handleBulkLeaveRequest={handleBulkLeaveRequest}
        handleBulkPrintLocal={handleBulkPrintLocal}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        employees={employees}
        handleMarkEmployeePresent={handleMarkEmployeePresent}
        handleMarkEmployeeAbsent={handleMarkEmployeeAbsent}
        handleMarkEmployeeLeave={handleMarkEmployeeLeave}
        handleViewEmployeeDetail={handleViewEmployeeDetail}
        handleMarkSelectedPresent={() => handleBulkAction('present')}
        handleMarkSelectedAbsent={() => handleBulkAction('absent')}
        loadEmployeeAttendance={loadEmployeeAttendance}
        handleMarkEmployeeStatusWithTimes={handleMarkEmployeeStatusWithTimes}
        reportDate={reportData?.date}
      />

      {/* Absent/Leave Employees Dialog */}
      <Dialog
        open={showAbsentDialog}
        onClose={() => setShowAbsentDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A2E14',
            border: '2px solid #D4AF37',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
          {selectedAbsentEmployees[0]?.leave_type ? 'Employees on Leave' : 'Absent Employees'} - {formatDate(selectedDate)}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedAbsentEmployees.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                    {!selectedAbsentEmployees[0]?.leave_type && isAdmin && (
                      <TableCell sx={{ fontWeight: 600, color: '#D4AF37', width: 50 }}>
                        <Checkbox
                          checked={selectedEmployeesForMarking.length === selectedAbsentEmployees.filter(emp => !emp.leave_type).length && selectedAbsentEmployees.filter(emp => !emp.leave_type).length > 0}
                          indeterminate={selectedEmployeesForMarking.length > 0 && selectedEmployeesForMarking.length < selectedAbsentEmployees.filter(emp => !emp.leave_type).length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployeesForMarking(selectedAbsentEmployees.filter(emp => !emp.leave_type).map(emp => emp.id || emp.employee_id));
                            } else {
                              setSelectedEmployeesForMarking([]);
                            }
                          }}
                          sx={{ color: '#D4AF37' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                    {selectedAbsentEmployees[0]?.leave_type && (
                      <>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Leave Type</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Reason</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAbsentEmployees.map((emp, idx) => (
                    <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: alpha('#D4AF37', 0.03) } }}>
                      {!emp.leave_type && isAdmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedEmployeesForMarking.includes(emp.id || emp.employee_id)}
                            onChange={(e) => {
                              const employeeId = emp.id || emp.employee_id;
                              if (e.target.checked) {
                                setSelectedEmployeesForMarking(prev => [...prev, employeeId]);
                              } else {
                                setSelectedEmployeesForMarking(prev => prev.filter(id => id !== employeeId));
                              }
                            }}
                            sx={{ color: '#D4AF37' }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ color: '#B4C4A8' }}>{emp.employee_id}</TableCell>
                      <TableCell sx={{ color: '#FFFFFF' }}>{emp.name || emp.employee_name}</TableCell>
                      <TableCell sx={{ color: '#B4C4A8' }}>{emp.department || emp.employee_details?.department || '-'}</TableCell>
                      {emp.leave_type && (
                        <>
                          <TableCell>
                            <Chip
                              label={emp.leave_type}
                              size="small"
                              sx={{ bgcolor: alpha('#2196F3', 0.2), color: '#90CAF9' }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#B4C4A8' }}>{emp.reason || '-'}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ bgcolor: '#1A2E14', color: '#B4C4A8', border: '1px solid #D4AF37' }}>No employees found.</Alert>
          )}
        </DialogContent>
        {!selectedAbsentEmployees[0]?.leave_type && isAdmin && selectedAbsentEmployees.length > 0 && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="contained"
              onClick={handleMarkSelectedPresent}
              disabled={markingPresent || selectedEmployeesForMarking.length === 0}
              startIcon={markingPresent ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              sx={{
                bgcolor: '#4CAF50',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#45a049' },
                '&:disabled': { bgcolor: '#666' }
              }}
            >
              {markingPresent ? 'Marking...' : `Mark Selected Present (${selectedEmployeesForMarking.length})`}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Employee Detail Dialog */}
      <Dialog
        open={showEmployeeDetailDialog}
        onClose={() => setShowEmployeeDetailDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A2E14',
            border: '2px solid #D4AF37',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
          Attendance Details - {selectedEmployeeDetail?.first_name} {selectedEmployeeDetail?.last_name} ({getMonthName(selectedMonth)} {selectedYear})
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loadingEmployeeDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#D4AF37' }} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#D4AF37', 0.1) }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: new Date(selectedYear, selectedMonth + 1, 0).getDate() }, (_, i) => i + 1).map(day => {
                    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const dateObj = new Date(dateStr);
                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                    const isFuture = dateObj > new Date();
                    const att = employeeDetailAttendance.find(a => new Date(a.date).toISOString().split('T')[0] === dateStr);
                    const status = att ? att.status : 'no_record';
                    const statusColor = getStatusColor(status);
                    return (
                      <TableRow key={day} hover={!isFuture} sx={{ '&:hover': !isFuture ? { bgcolor: alpha('#D4AF37', 0.03) } : {} }}>
                        <TableCell sx={{ color: '#FFFFFF' }}>{dateStr}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{dayName}</TableCell>
                        <TableCell>
                          {status === 'no_record' ? (
                            <Chip label={isFuture ? 'Future Date' : 'No Record'} size="small" sx={{ bgcolor: alpha('#9E9E9E', 0.2), color: '#B4C4A8' }} />
                          ) : (
                            <Chip
                              icon={statusColor.icon}
                              label={status.replace('_', ' ').toUpperCase()}
                              size="small"
                              sx={{ bgcolor: statusColor.bg, color: statusColor.color }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {isFuture ? (
                            <Typography sx={{ color: '#B4C4A8', fontSize: '0.8rem' }}>Not Available</Typography>
                          ) : att ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDayDetail(att)}
                              sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.7rem', borderColor: '#D4AF37', color: '#D4AF37' }}
                            >
                              View Details
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleMarkEmployeeStatus(selectedEmployeeDetail.id, dateStr, 'present')}
                                sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.6rem', borderColor: '#4CAF50', color: '#81C784' }}
                              >
                                Present
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleMarkEmployeeStatus(selectedEmployeeDetail.id, dateStr, 'absent')}
                                disabled={dateObj > new Date()}
                                sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.6rem', borderColor: '#F44336', color: '#EF9A9A' }}
                              >
                                Absent
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleMarkEmployeeStatus(selectedEmployeeDetail.id, dateStr, 'on_leave')}
                                sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.6rem', borderColor: '#2196F3', color: '#90CAF9' }}
                              >
                                Leave
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowEmployeeDetailDialog(false)}
            sx={{ color: '#B4C4A8' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Day Detail Dialog with Selfies */}
      <Dialog
        open={showDayDetailDialog}
        onClose={() => setShowDayDetailDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A2E14',
            border: '2px solid #D4AF37',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
          Attendance Details - {selectedDayRecord?.employee?.first_name} {selectedDayRecord?.employee?.last_name} ({formatDate(selectedDayRecord?.date)})
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDayRecord && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: '#D4AF37', color: '#1A2E14', fontSize: 28, fontWeight: 600 }}>
                  {selectedDayRecord?.employee?.first_name?.[0]}{selectedDayRecord?.employee?.last_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                    {selectedDayRecord?.employee?.first_name} {selectedDayRecord?.employee?.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                    {selectedDayRecord?.employee?.employee_id} | {selectedDayRecord?.employee?.department}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={selectedDayRecord?.status?.replace('_', ' ').toUpperCase()}
                      sx={{
                        bgcolor: getStatusColor(selectedDayRecord?.status).bg,
                        color: getStatusColor(selectedDayRecord?.status).color,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Login Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {selectedDayRecord?.login_time ? new Date(selectedDayRecord.login_time).toLocaleTimeString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Logout Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {selectedDayRecord?.logout_time ? new Date(selectedDayRecord.logout_time).toLocaleTimeString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Working Hours</Typography>
                  <Typography variant="body2" sx={{ color: '#81C784', fontWeight: 600 }}>
                    {formatWorkingHours(selectedDayRecord?.net_working_time || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                  <Typography variant="body2" sx={{ color: '#FFB74D', fontWeight: 600 }}>
                    {formatWorkingHours(selectedDayRecord?.total_break_time || 0)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Selfies Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#D4AF37' }}>
                  <Visibility sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                  Attendance Selfies
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {selectedDayRecord?.login_selfie ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>
                        Login Selfie
                      </Typography>
                      <img
                        src={`${baseURL}${selectedDayRecord.login_selfie}`}
                        alt="Login selfie"
                        className="selfie-large"
                        onClick={() => window.open(`${baseURL}${selectedDayRecord.login_selfie}`, '_blank')}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', opacity: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#B4C4A8' }}>No login selfie</Typography>
                    </Box>
                  )}
                  {selectedDayRecord?.logout_selfie ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>
                        Logout Selfie
                      </Typography>
                      <img
                        src={`${baseURL}${selectedDayRecord.logout_selfie}`}
                        alt="Logout selfie"
                        className="selfie-large"
                        onClick={() => window.open(`${baseURL}${selectedDayRecord.logout_selfie}`, '_blank')}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', opacity: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#B4C4A8' }}>No logout selfie</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Breaks Section */}
              {selectedDayRecord?.breaks && selectedDayRecord.breaks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#D4AF37' }}>
                    <Coffee sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                    Break Details ({selectedDayRecord.breaks.length})
                  </Typography>
                  {selectedDayRecord.breaks.map((brk, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,183,77,0.1)', border: '1px solid rgba(255,183,77,0.3)', borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Break Type</Typography>
                          <Typography variant="body2" sx={{ color: '#FFB74D', textTransform: 'capitalize' }}>
                            {brk.break_type?.replace('_', ' ')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Duration</Typography>
                          <Typography variant="body2" sx={{ color: '#FFB74D' }}>
                            {formatWorkingHours(brk.duration || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>Start Time</Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {brk.start_time ? new Date(brk.start_time).toLocaleTimeString() : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8' }}>End Time</Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {brk.end_time ? new Date(brk.end_time).toLocaleTimeString() : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>Start Selfie</Typography>
                          {brk.selfie_start ? (
                            <img
                              src={`${baseURL}${brk.selfie_start}`}
                              alt="Break start"
                              className="selfie-small"
                              onClick={() => window.open(`${baseURL}${brk.selfie_start}`, '_blank')}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>No selfie</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: '#B4C4A8', display: 'block', mb: 0.5 }}>End Selfie</Typography>
                          {brk.selfie_end ? (
                            <img
                              src={`${baseURL}${brk.selfie_end}`}
                              alt="Break end"
                              className="selfie-small"
                              onClick={() => window.open(`${baseURL}${brk.selfie_end}`, '_blank')}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>No selfie</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowDayDetailDialog(false)}
            sx={{ color: '#B4C4A8' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bar Chart Report Card */}
      {showBarChart && chartData && chartData.length > 0 && (
        <Card sx={{ mt: 3, bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardHeader
            title={<Typography sx={{ color: '#D4AF37', fontWeight: 600 }}>Attendance Summary Chart</Typography>}
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowBarChart(false)}
                sx={{ color: '#B4C4A8', borderColor: '#D4AF37' }}
              >
                Close
              </Button>
            }
          />
          <CardContent>
            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
              {chartData.map((item, index) => (
                <Tooltip key={index} title={`${item.employee}: ${item.present} present, ${item.late} late, ${item.absent} absent, ${item.onLeave} on leave`}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#B4C4A8', mb: 1, display: 'block' }}>
                      {item.employee.length > 8 ? item.employee.substring(0, 8) + '..' : item.employee}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Box component="div" sx={{ width: 30, height: Math.max(item.present * 5, 10), bgcolor: '#4CAF50', borderRadius: '2px 2px 0 0' }} />
                      <Box component="div" sx={{ width: 30, height: Math.max(item.late * 5, 10), bgcolor: '#FF9800', borderRadius: '2px 2px 0 0' }} />
                      <Box component="div" sx={{ width: 30, height: Math.max(item.absent * 5, 10), bgcolor: '#F44336', borderRadius: '2px 2px 0 0' }} />
                      <Box component="div" sx={{ width: 30, height: Math.max(item.onLeave * 5, 10), bgcolor: '#2196F3', borderRadius: '2px 2px 0 0' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#9E9E9E', mt: 1, display: 'block' }}>
                      P{item.present} L{item.late} A{item.absent} Lv{item.onLeave}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Reports;
